import type { FC } from 'react';
import React, { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Link from '@atlaskit/link/link';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import AiOptIn from '@atlassian/ai-opt-in/dialog';

import { FooterCommentsSummaryCombined } from '@confluence/ai-comments-summary/entry-points/FooterCommentsSummaryCombined.ts';
import { CommentsSummarySource } from '@confluence/ai-comments-summary/entry-points/CommentsSummaryContent.ts';
import { PageCatchupComponent } from '@confluence/page-catchup/entry-points/PageCatchupStreamingEntryPoint.tsx';
import { QuickSummaryStreamingComponent } from '@confluence/quick-summary/entry-points/QuickSummaryStreamingEntryPoint.ts';
import { QuickSummaryUpsell } from '@confluence/quick-summary/entry-points/QuickSummaryUpsell.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { AIFloatingContextMenuGroup } from './AIFloatingContextMenuGroup.tsx';
import { PopupStateTypes, isFeaturePopupState } from './popupStateTypes.ts';

export const i18n: {
	aiFloatingContextMenuOptInDialogHeaderText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	aiFloatingContextMenuAdminOptInDialogBodyText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	aiFloatingContextMenuOptInDialogHeaderText: {
		id: 'ai-floating-context-menu.opt-in.dialog.header.text',
		defaultMessage: 'Work smarter, not harder',
		description: 'Header text for Quick Summary AI Opt In Dialog',
	},
	aiFloatingContextMenuAdminOptInDialogBodyText: {
		id: 'ai-floating-context-menu.admin.opt-in.dialog.body.text',
		defaultMessage:
			'Save your team time by reading an Al-generated summary instead of an entire page with <a>Atlassian Intelligence</a>. <br></br><br></br> Activate Atlassian Intelligence features for your users from Atlassian Administration.',
		description: 'Body text for Quick Summary AI Opt In Dialog',
	},
});

const ATLASSIAN_INTELLIGENCE_LEARN_MORE_LINK =
	'https://support.atlassian.com/organization-administration/docs/understand-atlassian-intelligence-features-in-products';

const FeatureContentWidthStyles = xcss({
	width: '500px',
});

type AIFloatingContextMenuPopupContentProps = {
	popupState: PopupStateTypes;
	contentId: string;
	contentType: string;
	spaceKey: string;
	setIsOpen: (state: boolean) => void;
	setPopupState: (popupKey: PopupStateTypes) => void;
	lastModifiedDate: string;
	source: string;
	onClose: () => void;
	isLivePage?: boolean;
};

export const AIFloatingContextMenuPopupContent: FC<AIFloatingContextMenuPopupContentProps> = memo(
	({
		popupState,
		contentId,
		contentType,
		spaceKey,
		lastModifiedDate,
		source,
		onClose,
		setIsOpen,
		setPopupState,
		isLivePage = false,
	}) => {
		const intl = useIntl();
		const { orgId } = useSessionData();
		const adminLink = `https://admin.atlassian.com/o/${orgId}/atlassian-intelligence`;

		const formatOptInMessageValues = {
			a: (s: React.ReactNode[]) => (
				<Link href={ATLASSIAN_INTELLIGENCE_LEARN_MORE_LINK} target="_blank">
					{s}
				</Link>
			),
			br: () => <br />,
		};

		const renderContent = () => {
			switch (popupState) {
				case PopupStateTypes.Comments:
					return (
						<FooterCommentsSummaryCombined
							contentId={contentId}
							contentType={contentType}
							source={CommentsSummarySource.Header}
							spaceKey={spaceKey}
						/>
					);

				case PopupStateTypes.PageCatchup:
					return (
						<PageCatchupComponent
							contentId={contentId}
							contentType={contentType}
							spaceKey={spaceKey}
							setIsOpen={onClose}
							contentPath={window.location.pathname}
						/>
					);
				case PopupStateTypes.QuickSummary:
					return (
						<QuickSummaryStreamingComponent
							contentId={contentId}
							spaceKey={spaceKey}
							contentType={contentType}
							lastModifiedDate={lastModifiedDate}
							setIsOpen={onClose}
							source={source}
							contentPath={window.location.pathname}
						/>
					);
				case PopupStateTypes.Upsell:
					return <QuickSummaryUpsell />;

				case PopupStateTypes.OptIn:
					return (
						<AiOptIn
							source="quickSummary"
							product="confluence"
							isOrgAdmin
							headerText={intl.formatMessage(i18n.aiFloatingContextMenuOptInDialogHeaderText)}
							adminBodyText={intl.formatMessage(
								i18n.aiFloatingContextMenuAdminOptInDialogBodyText,
								formatOptInMessageValues,
							)}
							adminLink={adminLink}
							onAdminClose={() => setIsOpen(false)}
						/>
					);
				default:
					return (
						<AIFloatingContextMenuGroup
							contentId={contentId}
							setPopupState={setPopupState}
							setIsOpen={setIsOpen}
							isLivePage={isLivePage}
						/>
					);
			}
		};

		return (
			<Box xcss={[isFeaturePopupState(popupState) && FeatureContentWidthStyles]}>
				{renderContent()}
			</Box>
		);
	},
);
